import { Alert, Button, Checkbox, Container, FormControlLabel, IconButton, InputLabel, Step, StepLabel, Stepper, styled, TextField, Tooltip, tooltipClasses, TooltipProps } from '@mui/material';
import { Form, NavLink, useNavigate, useParams } from "react-router-dom";
import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import dayjs, { Dayjs } from 'dayjs';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Buffer } from "buffer";

import { AppSetting } from '../../../types/appSetting';
import { ITransporter, ITransporterLight } from '../../../types/transporter';
import { IVendor, IVendorLight } from '../../../types/vendor';
import { ICustomer, ICustomerLocation } from '../../../types/customer';
import { ICirbaBolUploadRequest, ILoad, ILoadMaterial } from "../../../types/load";
import { IManifest } from '../../../types/manifest';
import { IMaterialPrice } from '../../../types/material';
import { ISelectOption } from '../../../types/list';
import { IGeneratedDocument, IVendorDocument } from '../../../types/document';

import { AppConfig } from '../../../config/appConfig';
import { useUserContext } from "../../../contexts/userContext";
import { ErrorProvider } from '../../../classes/errorProvider';
import { ExternalLoadStateManager } from '../../../classes/externalLoadStateManager';

import { AppSettingService } from "../../../services/appSettingService";
import { TransporterService } from '../../../services/transporterService';
import { VendorService } from '../../../services/vendorService';
import { CustomerService } from '../../../services/customerService';
import { LoadService } from "../../../services/loadService";
import { ManifestService } from '../../../services/manifestService';
import { MaterialPriceService } from '../../../services/materialPriceService';
import { TrailerService } from '../../../services/trailerService';
import { UtilityService } from '../../../services/utilityService';

import LoadMaterialsForm from '../../forms/loadMaterialsForm';
import LoadManifestsForm from '../../forms/loadManifestsForm';
import PermissionGate from "../../permissionGate";
import Navigation from "../../header";
import BillOfLading from '../../billOfLading';
import Spinner from '../../spinner';
import { Icons } from '../../icons';
import VendorDocumentViewer from '../../vendorDocumentViewer';
import WorkingModal, { IWorkingModalProps } from '../../workingModal';
import DocumentViewer from '../../documentViewer';
import CameraDialog from '../../shared/cameraDialog';

const invalidContactMessage = "The contact email address is invalid. Please notify an administrator.";
const missingDeleteReasonMessage = "Please provide a reason for deleting this load.";

import "../../../styles/loadEdit.scss";

const LoadEdit = () => {
	const userContext = useUserContext();
	let { id } = useParams();
	const navigate = useNavigate();
	const [load, setLoad] = useState<ILoad>();
	const [transporter, setTransporter] = useState<ITransporter | undefined>(undefined);
	const [vendor, setVendor] = useState<IVendor | undefined>(undefined);
	const [manifestVendors, setManifestVendors] = useState<ISelectOption[]>([]);
	const [secondaryTransporters, setSecondaryTransporters] = useState<ITransporterLight[]>([]);
	const [customer, setCustomer] = useState<ICustomer | undefined>(undefined);
	const [materialPrices, setMaterialPrices] = useState<IMaterialPrice[] | undefined>(undefined);
	const [customerLocations, setCustomerLocations] = useState<ICustomerLocation[]>([]);
	const [manifests, setManifests] = useState<IManifest[]>([]);
	const [isLoadingLoad, setIsLoadingLoad] = useState(true);
	const [isLoadingTransporter, setIsLoadingTransporter] = useState(false);
	const [isLoadingVendor, setIsLoadingVendor] = useState(false);
	const [isLoadingManifestVendors, setIsLoadingManifestVendors] = useState(false);
	const [isLoadingSecondaryTransporters, setIsLoadingSecondaryTransporters] = useState(false);
	const [isLoadingCustomer, setIsLoadingCustomer] = useState(false);
	const [isLoadingCustomerLocations, setIsLoadingCustomerLocations] = useState(false);
	const [isLoadingManifests, setIsLoadingManifests] = useState(false);
	const [isCheckingVendorBol, setIsCheckingVendorBol] = useState(true);
	const [hasCheckedVendorBol, setHasCheckedVendorBol] = useState(false);
	const [isDownloadingVendorBol, setIsDownloadingVendorBol] = useState(false);
	const [vendorBol, setVendorBol] = useState<IVendorDocument | undefined>(undefined);
	const [isRedwoodShipmentCancelled, setIsRedwoodShipmentCancelled] = useState(false);
	const [disableWorkflowButtons, setDisableWorkflowButtons] = useState(false);
	const [activeTab, setActiveTab] = useState("details");
	const [scheduledPickupDate, setScheduledPickupDate] = useState(new Date);
	const [scheduledRedwoodPickupDate, setScheduledRedwoodPickupDate] = useState(new Date);
	const [confirmedPickupDate, setConfirmedPickupDate] = useState(new Date);
	const [errorOnCloseLoad, setErrorOnCloseLoad] = useState<string | undefined>(undefined);
	const [errorOnSchedulePickup, setErrorOnSchedulePickup] = useState<string | undefined>(undefined);
	const [errorOnConfirmPickup, setErrorOnConfirmPickup] = useState<string | undefined>(undefined);
	const [errorOnDelete, setErrorOnDelete] = useState<string | undefined>(undefined);
	const [errorOnUpdatingTrailerNumber, setErrorOnUpdatingTrailerNumber] = useState<string | undefined>(undefined);
	const [sealNumber, setSealNumber] = useState<string>('');
	const [sealNotRequired, setSealNotRequired] = useState<boolean>(false);
	const [showRealDeleteButton, setShowRealDeleteButton] = useState(false);
	const [deleteReason, setDeleteReason] = useState<string | undefined>(undefined);
	const [earliestAllowedDate, setEarliestAllowedDate] = useState<Dayjs>(dayjs("2025-4-01"));
	const [isVerifyingReturnToLoading, setIsVerifyingReturnToLoading] = useState(false);
	const [isVerifyingDeleteLoad, setIsVerifyingDeleteLoad] = useState(false);
	const [source, setSource] = useState<string>("manual");
	const [isEditingTrailerNumber, setIsEditingTrailerNumber] = useState(false);
	const [isSavingTrailerNumber, setIsSavingTrailerNumber] = useState(false);
	const [newTrailerNumber, setNewTrailerNumber] = useState<string>("");
	const [stateManager, setStateManager] = useState<ExternalLoadStateManager | undefined>(undefined);
	const [workingModalParams, setWorkingModalParams] = useState<IWorkingModalProps | undefined>(undefined);
	const [cirbaBolFilename, setCirbaBolFilename] = useState<string>("");
	const [cirbaBolDocument, setCirbaBolDocument] = useState<File | null>(null);
	const [cirbaBolNumber, setCirbaBolNumber] = useState<string>("");
	const [requireSignedBol, setRequireSignedBol] = useState<boolean>(false);
	const [signedBolDocFilename, setSignedBolDocFileName] = useState<string>('');
	const [signedBolDocFile, setSignedBolDocFile] = useState<File | null>(null);
	const [showSignedBolCameraDialog, setShowSignedBolCameraDialog] = useState<boolean>(false);
	const [showLoadCommunicationActions, setShowLoadCommunicationActions] = useState<boolean>(false);

	const steps = [
		"Loading",
		"Ready",
		"Scheduled",
		"Shipped"
	]
	const [activeStep, setActiveStep] = useState(0);

	const signedBolDocRef = useRef<HTMLInputElement>(null);
	const trailerNumberRef = useRef<HTMLInputElement>(null);

	const StyledTooltip = styled(({ className, ...props }: TooltipProps) => (
		<Tooltip describeChild {...props} arrow classes={{ popper: className }} />
	))(({ theme }) => ({
		[`& .${tooltipClasses.arrow}`]: {
			color: theme.palette.common.black,
		},
		[`& .${tooltipClasses.tooltip}`]: {
			backgroundColor: theme.palette.common.black,
			fontSize: theme.typography.pxToRem(14),
			maxWidth: 'none',
			padding: theme.spacing(1),
		},
	}));

	useEffect(() => {
		getAppSettings();
		getSecondaryTransporters();
	}, [])

	useEffect(() => {
		if (id) {
			getLoad();
			getManifests();
		}
	}, [id])

	useEffect(() => {
		if (customer?.details?.closedThroughDate) {
			// Earliest date is the day after the locked date
			setEarliestAllowedDate(dayjs(customer.details.closedThroughDate).add(1, 'day'));
		}
	}, [customer]);

	// Load the Vendor BOL if the load is refreshed while on the workflow tab. It would normally only trigger when switching tabs
	useEffect(() => {
		if (!isLoadingLoad && load && ['ready', 'scheduled', 'completed'].includes(load.status?.toLowerCase()) && activeTab == "workflow") {
			handleRedwoodBolStatus();
			handleCirbaBolStatus();
		}
	}, [stateManager]);

	useEffect(() => {
		if (isEditingTrailerNumber && trailerNumberRef?.current) {
			trailerNumberRef.current.focus();
		}
	}, [isEditingTrailerNumber]);

	useEffect(() => {
		if (!isLoadingVendor && !isLoadingLoad && vendor && load) {
			setStateManager(new ExternalLoadStateManager(vendor, load));
		}
	}, [isLoadingVendor, isLoadingLoad, vendor, load]);

	useEffect(() => {
		if (load) {
			getCustomerLocations(load.customerId);
			getManifestVendors();
		}
	}, [load])

	const getAppSettings = async () => {
		// Low priority, no loading statuses necessary
		const requireSignedBolSetting = (await AppSettingService.getAppSettingByName("RequireSignedBols"))?.data;
		setRequireSignedBol(AppSettingService.mapAppSetting(requireSignedBolSetting).valueAsBoolean());

		const loadCommunicationsSetting = (await AppSettingService.getAppSettingByName("EnableLoadCommunicationsForVendorsAndTransporters"))?.data;
		setShowLoadCommunicationActions(AppSettingService.mapAppSetting(loadCommunicationsSetting).valueAsBoolean());
	}

	const getLoad = async () => {
		if (id) {
			setIsLoadingLoad(true);
			LoadService.getLoad(id)
				.then((response) => {
					console.log('LoadService.getLoad: SUCCESS', response)
					const newLoad = LoadService.mapLoad(response.data);
					determineActiveStep(newLoad.status);

					setLoad(newLoad);
					getMaterialPrices(newLoad.customerId, newLoad.vendorId);
					getTransporter(newLoad.transporterId);
					getVendor(newLoad.vendorId);
					getCustomer(newLoad.customerId);
				})
				.catch((error) => {
					console.log('LoadService.getLoad: ERROR', error)
				})
				.finally(() => {
					setIsLoadingLoad(false);
				});
		}
	}

	const getTransporter = async (transporterId) => {
		if (!transporterId) {
			console.log(`TransporterService.getTransporter: There is no TransporterId for load ${id}. Skipping. `)
			return;
		}
		setIsLoadingTransporter(true);
		TransporterService.getTransporter(transporterId)
			.then((response) => {
				console.log('TransporterService.getTransporter: SUCCESS', response)
				setTransporter(TransporterService.mapTransporter(response.data));
			})
			.catch((error) => {
				console.log('TransporterService.getTransporter: ERROR', error)
			})
			.finally(() => {
				setIsLoadingTransporter(false);
			});

	}

	const getVendor = async (vendorId) => {
		setIsLoadingVendor(true);
		VendorService.getOnlyVendor(vendorId)
			.then((response) => {
				console.log('VendorService.getVendor: SUCCESS', response)
				setVendor(VendorService.mapVendor(response.data));
			})
			.catch((error) => {
				console.log('VendorService.getVendor: ERROR', error)
			})
			.finally(() => {
				setIsLoadingVendor(false);
			});
	}

	const getCustomer = async (customerId) => {
		setIsLoadingCustomer(true);
		CustomerService.getCustomer(customerId)
			.then((response) => {
				console.log('CustomerService.getCustomer: SUCCESS', response)
				setCustomer(CustomerService.mapCustomer(response.data));
			})
			.catch((error) => {
				console.log('CustomerService.getCustomer: ERROR', error)
			})
			.finally(() => {
				setIsLoadingCustomer(false);
			});
	}

	const getMaterialPrices = async (customerId, vendorId) => {
		MaterialPriceService.getMaterialPricesForLoadForm(customerId, vendorId)
			.then((response) => {
				console.log('MaterialService.getMaterialsForLoadForm: SUCCESS', response)

				//Had to add a tare material back into the system so Quiana could reconcile old loads
				let excludingTareItems = response.data.filter((item) => { return item.materialId == 3922 ? false : true; })

				setMaterialPrices(excludingTareItems);
			})
			.catch((error) => {
				console.log('MaterialService.getMaterialsForLoadForm: ERROR', error)
			});
	}

	const getCustomerLocations = async (customerId) => {
		setIsLoadingCustomerLocations(true);

		CustomerService.getCustomerLocationsByCustomerId(customerId)
			.then((response) => {
				console.log('CustomerService.getCustomerLocationsByCustomerId: SUCCESS', response)
				setCustomerLocations(CustomerService.mapCustomerLocations(response.data));
			})
			.catch((error) => {
				console.log('CustomerService.getCustomerLocationsByCustomerId: ERROR', error)
			})
			.finally(() => {
				setIsLoadingCustomerLocations(false);
			});
	}

	const getManifests = async () => {
		setIsLoadingManifests(true);

		ManifestService.getManifestsByLoad(id)
			.then((response) => {
				console.log('ManifestService.getManifestsByLoad: SUCCESS', response)
				setManifests(ManifestService.mapManifests(response.data).sort((a, b) => UtilityService.compareStrings(a.number, b.number)));
			})
			.catch((error) => {
				console.log('ManifestService.getManifestsByLoad: ERROR', error)
			})
			.finally(() => {
				setIsLoadingManifests(false);
			});
	}

	const getManifestVendors = async () => {
		setIsLoadingManifestVendors(true);

		VendorService.getVendorsByCustomerId(load.customerId)
			.then((response) => {
				console.log('VendorService.getVendorsByCustomerId: SUCCESS', response)
				setManifestVendors((response.data as IVendorLight[]).map(v => ({ name: v.name, value: v.id.toString() })).sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('VendorService.getVendorsByCustomerId: ERROR', error)
			})
			.finally(() => {
				setIsLoadingManifestVendors(false);
			});
	}

	const getSecondaryTransporters = async () => {
		setIsLoadingSecondaryTransporters(true);

		TransporterService.getTransporters()
			.then((response) => {
				console.log('TransporterService.getTransporters: SUCCESS', response)
				setSecondaryTransporters(
					TransporterService.mapTransporterLightList(response.data)
						.filter((item) => item.status)
						.sort((a, b) => UtilityService.compareStrings(a.name, b.name)));
			})
			.catch((error) => {
				console.log('TransporterService.getTransporters: ERROR', error)
			})
			.finally(() => {
				setIsLoadingSecondaryTransporters(false);
			});
	}

	const determineActiveStep = (status: string) => {
		switch (status?.toLowerCase()) {
			case "ready": {
				setActiveStep(1);
				break;
			}
			case "scheduled": {
				setActiveStep(2);
				break;
			}
			case "shipped": {
				setActiveStep(3);
				break;
			}
			case "legacy": {
				setActiveStep(3);
				break;
			}
			default: {
				setActiveStep(0);
				break;
			}
		}
	}

	const handleManifestsUpdated = (manifests: IManifest[]) => {
		setManifests(manifests.sort((a, b) => UtilityService.compareStrings(a.number, b.number)));
	}

	const handleMaterialsUpdated = (materials: ILoadMaterial[]) => {
		setLoad({ ...load, materials: materials });
	}

	const handleCloseLoad = () => {
		setDisableWorkflowButtons(true);
		setErrorOnCloseLoad(undefined);

		LoadService.close(load.id, sealNumber)
			.then((response) => {
				console.log('LoadService.close: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.close: ERROR', error);
				setErrorOnCloseLoad(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
			});
	}

	const handleBreakSeal = () => {
		setDisableWorkflowButtons(true);
		setErrorOnCloseLoad(undefined);

		LoadService.breakSeal(load.id)
			.then((response) => {
				console.log('LoadService.breakSeal: SUCCESS', response)
				getLoad();
				setSealNumber('');
				setSealNotRequired(false);
			})
			.catch((error) => {
				console.log('LoadService.breakSeal: ERROR', error);
				setErrorOnCloseLoad(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
			});
	};

	const handleReturnToLoading = () => {
		setDisableWorkflowButtons(true);
		setErrorOnSchedulePickup(undefined);

		LoadService.returnToLoading(load.id)
			.then((response) => {
				console.log('LoadService.returnToLoading: SUCCESS', response)
				getLoad();
				setSealNumber('');
				setSealNotRequired(false);
			})
			.catch((error) => {
				console.log('LoadService.returnToLoading: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setIsVerifyingReturnToLoading(false);
			});
	};

	const handleSchedulePickup = (bypassVendor?: string) => {
		setDisableWorkflowButtons(true);
		setErrorOnSchedulePickup(undefined);

		LoadService.schedule(load.id, scheduledPickupDate, bypassVendor)
			.then((response) => {
				console.log('LoadService.schedule: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.schedule: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
			});
	}

	const handleScheduleRedwoodPickup = () => {
		setDisableWorkflowButtons(true);
		setErrorOnSchedulePickup(undefined);
		setWorkingModalParams({ params: { content: "Scheduling pickup with Redwood..." } });

		LoadService.scheduleRedwood(load.id, scheduledRedwoodPickupDate)
			.then((response) => {
				console.log('LoadService.scheduleRedwood: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.scheduleRedwood: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleRedwoodBolStatus = (thisLoad?: ILoad) => {
		const useLoad = thisLoad ?? load;
		if (!(useLoad && useLoad.isRedwood)) return;
		switch (useLoad.status?.toLowerCase()) {
			case "ready": {
				checkRedwoodStatus();
				break;
			}
			case "scheduled":
			case "shipped": {
				downloadRedwoodBol();
				break;
			}
		}
	}

	const checkRedwoodStatus = () => {
		if (hasCheckedVendorBol) return;
		if (load.isRedwood && !load.loadDataSentToVendor) return;

		setDisableWorkflowButtons(true);
		setIsCheckingVendorBol(true);

		LoadService.getRedwoodStatus(load.id)
			.then((response) => {
				console.log('LoadService.getRedwoodStatus: SUCCESS', response)
				const status = response.data.toString().toLowerCase();

				// Switching from 'delivered-to-redwood- to just 'delivered'. Support both for now but remove the former when applicable
				if (status == 'in-progress' || status == 'delivered-to-redwood' || status == 'delivered') {
					getLoad();
				} else {
					if (status == 'cancelled') {
						setIsRedwoodShipmentCancelled(true);
					}
					setIsCheckingVendorBol(false);
				}
			})
			.catch((error) => {
				setIsCheckingVendorBol(false);
				console.log('LoadService.getRedwoodStatus: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setHasCheckedVendorBol(true);
			});
	}

	const downloadRedwoodBol = () => {
		if (vendorBol) return;
		setIsDownloadingVendorBol(true);

		LoadService.getRedwoodBol(load.id)
			.then((response) => {
				console.log('LoadService.getRedwoodBol: SUCCESS', response)
				const document = response.data as IVendorDocument;
				document.uploadDate = new Date(document.uploadDate);
				setVendorBol(document);
			})
			.catch((error) => {
				console.log('LoadService.getRedwoodBol: ERROR', error);
			})
			.finally(() => {
				setIsDownloadingVendorBol(false);
			});
	}

	const handleSendWorkOrderToCirba = () => {
		setErrorOnSchedulePickup(undefined);
		setDisableWorkflowButtons(true);
		setWorkingModalParams({ params: { content: "Sending Work Order to Cirba..." } });

		LoadService.sendWorkOrderToCirba(load.id)
			.then((response) => {
				console.log('LoadService.sendWorkOrderToCirba: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.sendWorkOrderToCirba: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleCirbaBolFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setCirbaBolFilename(file.name);
			setCirbaBolDocument(file);
		}
	}

	const handleUploadCirbaBol = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (!cirbaBolDocument) {
			setWorkingModalParams({ params: { content: "No BOL document has been selected." } });
			return;
		}

		setErrorOnSchedulePickup(undefined);
		setDisableWorkflowButtons(true);
		setWorkingModalParams({ params: { content: "Uploading Cirba BOL..." } });

		const request: ICirbaBolUploadRequest = {
			loadId: load.id,
			bolNumber: cirbaBolNumber,
			content: cirbaBolDocument
		};

		LoadService.uploadCirbaBol(request)
			.then((response) => {
				console.log('LoadService.uploadCirbaBol: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.uploadCirbaBol: ERROR', error);
				setErrorOnSchedulePickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleCirbaBolStatus = () => {
		if (!stateManager.cirba) return;
		switch (load?.status?.toLowerCase()) {
			case "ready": {
				if (stateManager.cirba.isWaitingToSchedulePickup) {
					downloadCirbaBol();
				}
				break;
			}
			case "scheduled":
			case "shipped": {
				downloadCirbaBol();
				break;
			}
		}
	}

	const downloadCirbaBol = () => {
		if (vendorBol) return;
		setIsDownloadingVendorBol(true);

		LoadService.getCirbaBol(load.id)
			.then((response) => {
				console.log('LoadService.getCirbaBol: SUCCESS', response)
				const document = response.data as IVendorDocument;
				document.uploadDate = new Date(document.uploadDate);
				setVendorBol(document);
			})
			.catch((error) => {
				console.log('LoadService.getCirbaBol: ERROR', error);
			})
			.finally(() => {
				setIsDownloadingVendorBol(false);
			});
	}

	const handleConfirmPickup = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (requireSignedBol) {
			if (!signedBolDocFile) {
				setErrorOnConfirmPickup("A signed BOL document is required to confirm pickup.");
				return;
			}

			setWorkingModalParams({ params: { content: "Transferring signed BOL document..." } });
		}
		setErrorOnConfirmPickup(undefined);
		setDisableWorkflowButtons(true);

		const signedBolDocument: IGeneratedDocument | undefined = signedBolDocFile ? {
			name: signedBolDocFilename,
			contents: signedBolDocFile,
			contentType: signedBolDocFile.type
		} : undefined;

		LoadService.pickup(load.id, confirmedPickupDate, signedBolDocFile)
			.then((response) => {
				console.log('LoadService.pickup: SUCCESS', response)
				getLoad();
			})
			.catch((error) => {
				console.log('LoadService.pickup: ERROR', error);
				setErrorOnConfirmPickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setWorkingModalParams(undefined);
			});
	}

	const handleScheduledPickupDateChange = (value: Dayjs | null) => {
		if (value) {
			setScheduledPickupDate(value.toDate());
		}
	}

	const handleScheduledRedwoodPickupDateChange = (value: Dayjs | null) => {
		if (value) {
			setScheduledRedwoodPickupDate(value.toDate());
		}
	}

	const handleConfirmPickupDateChange = (value: Dayjs | null) => {
		if (value) {
			setConfirmedPickupDate(value.toDate());
		}
	}

	const handleUpdateSource = (source: string) => {
		setSource(source);
	}

	const handleDelete = () => {
		if (!deleteReason || deleteReason.trim().length == 0) {
			setErrorOnDelete(missingDeleteReasonMessage);
			return;
		}

		setDisableWorkflowButtons(true);
		setWorkingModalParams({ params: { content: "Deleting load..." } });

		LoadService.deleteLoad(load.id, deleteReason)
			.then((response) => {
				console.log('LoadService.deleteLoad: SUCCESS', response)
				navigate("/tils");
			})
			.catch((error) => {
				console.log('LoadService.deleteLoad: ERROR', error);
				setErrorOnDelete(ErrorProvider.getError(error));
			})
			.finally(() => {
				setDisableWorkflowButtons(false);
				setIsVerifyingDeleteLoad(false);
				setWorkingModalParams(undefined);
			});
	}

	const cancelDelete = () => {
		setShowRealDeleteButton(false);
		setErrorOnDelete(undefined);
	}

	const handleEditTrailerNumberClick = () => {
		setNewTrailerNumber(load?.trailerNumber ?? "");
		setIsEditingTrailerNumber(true);
	}

	const handleSaveTrailerNumberClick = () => {
		if (newTrailerNumber.trim().length == 0) return;
		setErrorOnUpdatingTrailerNumber(undefined);
		setIsSavingTrailerNumber(true);

		TrailerService.updateTrailerNumber(load.trailerId, newTrailerNumber.trim(), load.id)
			.then((response) => {
				console.log('TrailerService.updateTrailerNumber: SUCCESS', response)
				var trailer = TrailerService.mapTrailer(response.data);
				load.trailerNumber = trailer.trailerNumber;
				setIsEditingTrailerNumber(false);
			})
			.catch((error) => {
				console.log('TrailerService.updateTrailerNumber: ERROR', error);
				setErrorOnUpdatingTrailerNumber(ErrorProvider.getError(error));
			})
			.finally(() => {
				setIsSavingTrailerNumber(false);
			});
	}

	const handleTrailerNumberKeyDown = (event: React.KeyboardEvent) => {
		if (event.key === "Enter") {
			handleSaveTrailerNumberClick();
		} else if (event.key === "Escape") {
			setIsEditingTrailerNumber(false);
		}
	}

	const areAllHazmatsOnManifests = () => {
		return (load?.materials?.filter(m => m.isHazardous && !m.manifestId) ?? []).length == 0;
	}

	const handleSignedBolDocChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		if (event.target.files && event.target.files.length > 0) {
			const file = event.target.files[0];
			setSignedBolDocFile(file);
			setSignedBolDocFileName(file.name);
		}
	}

	useEffect(() => {
		if (load?.status?.toLowerCase() == "loading") {
			setDisableWorkflowButtons(!areAllHazmatsOnManifests());
		}
	}, [load?.status, areAllHazmatsOnManifests()])

	const handleOnTabChangeRequest = (tab: string) => {
		setActiveTab(tab);
	}

	const vendorHasContactEmails = (): boolean => {
		if (!vendor?.contact) return false;

		return (!!
			(vendor.contact.email?.trim() ||
				vendor.contact.email2?.trim() ||
				vendor.contact.email3?.trim() ||
				vendor.contact.email4?.trim()));
	}

	const transporterHasContactEmails = (): boolean => {
		if (!transporter?.contact) return false;

		return (!!
			(transporter.contact.email?.trim() ||
				transporter.contact.email2?.trim() ||
				transporter.contact.email3?.trim() ||
				transporter.contact.email4?.trim()));
	}

	const handleEmailLoadSheetToVendor = () => {
		setErrorOnConfirmPickup(undefined);

		setWorkingModalParams({ params: { content: `Sending load sheet to ${load.vendorName}...` } });
		LoadService.emailLoadSheetToVendor(load.id)
			.then((response) => {
				console.log('LoadService.emailLoadSheetToVendor: SUCCESS', response);
			})
			.catch((error) => {
				console.log('LoadService.emailLoadSheetToVendor: ERROR', error);
				if (error.response?.data?.includes("At least one recipient is not valid")) {
					setErrorOnConfirmPickup(invalidContactMessage);
				}
				else {
					setErrorOnConfirmPickup(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const handleNotifyTransporter = () => {
		setErrorOnConfirmPickup(undefined);

		setWorkingModalParams({ params: { content: `Sending notification to ${load.transporterName}...` } });
		LoadService.notifyTransporterOfLoadScheduled(load.id)
			.then((response) => {
				console.log('LoadService.notifyTransporterOfLoadScheduled: SUCCESS', response);
			})
			.catch((error) => {
				console.log('LoadService.notifyTransporterOfLoadScheduled: ERROR', error);
				if (error.response?.data?.includes("At least one recipient is not valid")) {
					setErrorOnConfirmPickup(invalidContactMessage);
				}
				else {
					setErrorOnConfirmPickup(ErrorProvider.getError(error));
				}
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const exportLoadSheet = () => {
		setErrorOnConfirmPickup(undefined);

		setWorkingModalParams({ params: { content: `Exporting load sheet...` } });
		LoadService.exportLoadMaterialListToExcel(load.id)
			.then((response) => {
				console.log('LoadService.exportLoadMaterialListToExcel: SUCCESS', response);
				downloadFromByteArray(response.data);
			})
			.catch((error) => {
				console.log('LoadService.exportLoadMaterialListToExcel: ERROR', error);
				setErrorOnConfirmPickup(ErrorProvider.getError(error));
			})
			.finally(() => {
				setWorkingModalParams(undefined);
			});
	}

	const downloadFromByteArray = (file: IGeneratedDocument): void => {
		const uint8ArrayData = new Uint8Array(Buffer.from(file.contents, 'base64'));
		const blob = new Blob([uint8ArrayData], { type: file.contentType });
		const url = window.URL.createObjectURL(blob);

		// Create a hidden anchor element to trigger the download
		const link = document.createElement('a');
		link.href = url;
		link.download = file.name;
		link.style.visibility = 'hidden';
		document.body.appendChild(link);

		// Simulate a click to start the download
		link.click();

		// Clean up the DOM and revoke the object URL (after a slight delay)
		document.body.removeChild(link);
		// A delay is often necessary to ensure the browser has initiated the download
		setTimeout(() => {
			window.URL.revokeObjectURL(url);
		}, 100);
	}

	const handeSignedBolCameraDialogCloseRequest = () => {
		setShowSignedBolCameraDialog(false);
	}

	const handleSignedBolSnapshotReady = (file: IGeneratedDocument) => {
		const filename = `Signed_BOL_${load.hwiNumber.replaceAll(/[^a-zA-Z0-9-]/g, "")}_${dayjs().format("YYYY-MM-DD")}.jpg`;
		setSignedBolDocFile(new File([file.contents], filename, { type: file.contentType }));
		setSignedBolDocFileName(filename);
	}

	return (
		<div className="hwi-load-edit">
			<Navigation></Navigation>
			<Container maxWidth="lg" className="route-container">
				<PermissionGate permission={userContext.permissions?.canManageLoads}>
					<Spinner dependency={isLoadingLoad || isLoadingVendor} text='Loading load details...'>
						<LocalizationProvider dateAdapter={AdapterDayjs}>
							<section className="management-page-header">
								<div className="title">
									<NavLink to="/tils">
										<Icons.GoBack fill="#9399ad" height="24px"></Icons.GoBack>
									</NavLink>
									Load Details
								</div>
								<div className="tabs">
									<div className={activeTab == "details" ? "tab active" : "tab"} onClick={() => setActiveTab("details")}>Details</div>
									<div className={activeTab == "materials" ? "tab active" : "tab"} onClick={() => setActiveTab("materials")}>Materials</div>
									<div className={activeTab == "manifests" ? "tab active" : "tab"} onClick={() => setActiveTab("manifests")}>Manifests</div>
									{userContext?.permissions?.canManageLoadWorkflow &&
										<div
											className={activeTab == "workflow" ? "tab active" : "tab"}
											onClick={() => {
												setActiveTab("workflow");
												if (stateManager.redwood) handleRedwoodBolStatus();
												if (stateManager.cirba) handleCirbaBolStatus();
											}}>
											Workflow
										</div>
									}
								</div>
							</section>
							<Spinner dependency={isLoadingLoad} text='Refreshing load details...'>
								{(activeTab == "details" && load) &&
									<div className="details">
										{errorOnUpdatingTrailerNumber &&
											<Alert className="alert"
												severity="error"
												action={<IconButton onClick={() => { setErrorOnUpdatingTrailerNumber(undefined); }} size='small'>X</IconButton>}>
												{errorOnUpdatingTrailerNumber}
											</Alert>
										}
										{errorOnDelete &&
											<Alert className="alert"
												severity="error"
												action={<IconButton onClick={() => { setErrorOnDelete(undefined); }} size='small'>X</IconButton>}>
												{errorOnDelete}
											</Alert>
										}
										<div className="row">
											<InputLabel>HWI / BOL Number</InputLabel>
											<TextField className="id-field" disabled fullWidth name="name" value={load?.hwiNumber} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Trailer Number</InputLabel>
											<div className="trailer-number-edit">
												{!isEditingTrailerNumber &&
													<TextField className="trailer-field" disabled fullWidth name="name" value={load?.trailerNumber} size="small"></TextField>}
												{isEditingTrailerNumber &&
													<TextField
														className="trailer-field"
														inputRef={trailerNumberRef}
														disabled={isSavingTrailerNumber}
														inputProps={{ maxLength: 50 }}
														fullWidth
														name="name"
														value={newTrailerNumber}
														onChange={(e) => setNewTrailerNumber(e.target.value)}
														onFocus={(e) => { e.target.select(); }}
														onKeyDown={handleTrailerNumberKeyDown}
														size="small"></TextField>}
												{!isEditingTrailerNumber &&
													<Button color="primary" variant="outlined" onClick={handleEditTrailerNumberClick}>Change Trailer #</Button>}
												{isEditingTrailerNumber &&
													<Button color="primary" variant="contained" disabled={isSavingTrailerNumber} onClick={handleSaveTrailerNumberClick}>{isSavingTrailerNumber ? "Updating..." : "Update"}</Button>}
												{isEditingTrailerNumber &&
													<Button color="error" variant="contained" disabled={isSavingTrailerNumber} onClick={() => setIsEditingTrailerNumber(false)}>Cancel</Button>}
											</div>
										</div>
										<div className="row">
											<InputLabel>Customer</InputLabel>
											<TextField className="customer-field" disabled fullWidth name="name" value={load?.customerName ?? ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Vendor</InputLabel>
											<TextField className="vendor-field" disabled fullWidth name="name" value={load?.vendorName ?? ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Transporter</InputLabel>
											<TextField className="transporter-field" disabled fullWidth name="name" value={load?.transporterName ?? ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Status</InputLabel>
											<TextField className="status-field" disabled fullWidth name="name" value={load?.status ?? ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Seal Number</InputLabel>
											<TextField className="status-field" disabled fullWidth name="name" value={load?.sealNumber ?? ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Scheduled Pickup Date</InputLabel>
											<TextField className="scheduled-field" disabled fullWidth name="name" value={load?.scheduledPickupDate ? moment(load?.scheduledPickupDate).format("MM/DD/YYYY") : ""} size="small"></TextField>
										</div>
										<div className="row">
											<InputLabel>Pickup Date</InputLabel>
											<TextField className="pickup-field" disabled fullWidth name="name" value={load?.pickupDate ? moment(load?.pickupDate).format("MM/DD/YYYY") : ""} size="small"></TextField>
										</div>
										<div className="row text-right">
											{showRealDeleteButton ? (
												<div className="delete-actions">
													<span>Are you sure you want to delete this load?</span>
													<TextField
														className={`deleted-reason-field ${errorOnDelete == missingDeleteReasonMessage ? "error" : ""}`}
														placeholder="Reason for deleting (Required)"
														disabled={disableWorkflowButtons}
														name="deletedReason"
														value={deleteReason ?? ""}
														onChange={(e) => setDeleteReason(e.target.value)}
														size="small"
													></TextField>
													<Button variant="contained" color='error' disabled={disableWorkflowButtons} onClick={handleDelete}>Yes: Delete Load</Button>
													<Button variant="contained" color='primary' disabled={disableWorkflowButtons} onClick={cancelDelete}>No: Cancel</Button>
												</div>

											) : (
												<Button variant="contained" color='error' disabled={disableWorkflowButtons} onClick={() => { setDeleteReason(undefined); setShowRealDeleteButton(true); }}>Delete Load</Button>
											)}
										</div>
									</div>
								}
								{activeTab == "materials" &&
									<div className="materials">
										{load && id && load?.customerId && load?.vendorId && customerLocations &&
											<LoadMaterialsForm
												loadId={id}
												customerId={load?.customerId}
												vendorId={load?.vendorId}
												manifests={manifests}
												isLoadingManifests={isLoadingManifests}
												materials={load?.materials ? load.materials : []}
												materialPrices={materialPrices}
												customerLocations={customerLocations}
												status={load?.status}
												source={source}
												updateSource={handleUpdateSource}
												updateLoadMaterials={(materials: ILoadMaterial[]) => { setLoad({ ...load, materials: materials }); }}
												onTabChangeRequest={handleOnTabChangeRequest}
											></LoadMaterialsForm>
										}
									</div>
								}
								{activeTab == "manifests" &&
									<div className="manifests">
										<LoadManifestsForm
											load={load}
											isLoadingLoad={isLoadingLoad}
											materials={load?.materials || []}
											manifests={manifests}
											isLoadingManifests={isLoadingManifests}
											manifestVendors={manifestVendors}
											isLoadingManifestVendors={isLoadingManifestVendors}
											secondaryTransporters={secondaryTransporters}
											isLoadingSecondaryTransporters={isLoadingSecondaryTransporters}
											manifestsUpdated={handleManifestsUpdated}
											materialsUpdated={handleMaterialsUpdated}
										></LoadManifestsForm>
									</div>
								}
								{activeTab == "workflow" &&
									<div className="workflow">
										<div className="stepper">
											<Stepper activeStep={activeStep} alternativeLabel>
												{steps.map((label) => (
													<Step key={label}>
														<StepLabel>{label}</StepLabel>
													</Step>
												))}
											</Stepper>
										</div>
										{(load.status?.toLowerCase() == "loading") && (
											<div className="loading">
												<div className="instructions">
													{errorOnCloseLoad &&
														<Alert className="alert"
															severity="error"
															action={<IconButton onClick={() => { setErrorOnCloseLoad(undefined); }} size='small'>X</IconButton>}>
															{errorOnCloseLoad}
														</Alert>
													}
													{!areAllHazmatsOnManifests() &&
														<Alert className="alert-danger"
															severity="error">
															All hazardous materials must be assigned to a manifest before this load can proceed to the next workflow step.
														</Alert>
													}
													<p className="title">
														This shipment is in <strong>Loading</strong> status. From this step you can:
													</p>
													<br></br>
													<p>Manage the contents of this load form the Materials tab.</p>
													<p><Button variant='contained' disabled={disableWorkflowButtons} onClick={() => setActiveTab("materials")}>Manage Load Contents</Button></p>
													<div className="option-divider">
														<hr className="line"></hr>
														<p className="copy"><span>OR</span></p>
													</div>
													<p>Close this load and get a Bill of Lading.</p>
													<p><TextField className='seal-field' placeholder='Seal Number' disabled={sealNotRequired} value={sealNumber} onChange={(e) => setSealNumber(e.target.value)}></TextField></p>
													<p>
														<FormControlLabel
															className='seal-checkbox-label no-margin'
															control={<Checkbox checked={sealNotRequired} onChange={(e) => { setSealNotRequired(e.target.checked); setSealNumber(''); }} />}
															label="This load does not require a seal." />
													</p>
													<p>
														<Tooltip
															title={'Please provide a seal number or indicate that one is not required to continue.'}
															placement='right'
															disableHoverListener={sealNotRequired || sealNumber.length > 0}
															arrow>
															<span>
																<Button variant='contained' color='error' disabled={(!sealNotRequired && sealNumber.length == 0) || disableWorkflowButtons} onClick={handleCloseLoad}>Close This Load</Button>
															</span>
														</Tooltip>
													</p>
												</div>
											</div>
										)}
										{(load.status?.toLowerCase() == "ready" && stateManager?.isStandard) && (
											<div className="ready">
												<div className="instructions">
													{errorOnSchedulePickup &&
														<Alert className="alert"
															severity="error"
															action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
															{errorOnSchedulePickup}
														</Alert>
													}
													<p className="title">
														This load is in <strong>Ready</strong> status. From this step you can:
													</p>
													<br></br>
													<p>Break the seal. This will remove the seal number (if applicable) and return the load to "Loading" status where alterations can be made.</p>
													<p><Button variant='contained' color='error' disabled={disableWorkflowButtons} onClick={handleBreakSeal}>Break Seal</Button></p>
													<div className="option-divider">
														<hr className="line"></hr>
														<p className="copy"><span>OR</span></p>
													</div>
													<p>Contact the transporter to schedule pickup then enter the scheduled pickup date below. Once you have scheduled a load for pickup you will no longer be able to break the seal.</p>
													<p className="controls">
														<DatePicker className="expiration-date-field" value={dayjs(scheduledPickupDate)} minDate={earliestAllowedDate} disabled={disableWorkflowButtons} onChange={(value) => handleScheduledPickupDateChange(value)} />
														<Button variant='contained' color='secondary' disabled={disableWorkflowButtons} onClick={() => handleSchedulePickup()}>Schedule Pickup</Button>
													</p>
													<br></br>
													<Spinner dependency={isLoadingCustomer || isLoadingTransporter || isLoadingVendor} text='Loading BOL...'>
														<BillOfLading load={load} transporter={transporter} vendor={vendor} customer={customer}></BillOfLading>
													</Spinner>
												</div>
											</div>
										)}
										{(load.status?.toLowerCase() == "ready" && stateManager?.redwood) && (
											<>
												{stateManager.redwood.isReadyToSchedule &&
													<div className="ready">
														<div className="instructions">
															{errorOnSchedulePickup &&
																<Alert className="alert"
																	severity="error"
																	action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
																	{errorOnSchedulePickup}
																</Alert>
															}
															<p className="title">
																This load is in <strong>Ready</strong> status. From this step you can:
															</p>
															<br></br>
															<p>Break the seal. This will remove the seal number (if applicable) and return the load to "Loading" status where alterations can be made.</p>
															<p><Button variant='contained' color='error' disabled={disableWorkflowButtons} onClick={handleBreakSeal}>Break Seal</Button></p>
															<div className="option-divider">
																<hr className="line"></hr>
																<p className="copy"><span>OR</span></p>
															</div>
															<p>Schedule this shipment directly with Redwood.</p>
															<p className="controls">
																<DatePicker className="expiration-date-field" value={dayjs(scheduledRedwoodPickupDate)} minDate={earliestAllowedDate} disabled={disableWorkflowButtons} onChange={(value) => handleScheduledRedwoodPickupDateChange(value)} />
																<Button variant='contained' color='secondary' disabled={disableWorkflowButtons} onClick={handleScheduleRedwoodPickup}>Schedule with Redwood</Button>
															</p>
															{userContext.permissions?.canBypassVendorWorkflow &&
																<>
																	<p>OR</p>
																	<p>Bypass Redwood scheduling and use HWI Bill of Lading.</p>
																	<p className="controls">
																		<DatePicker className="expiration-date-field" value={dayjs(scheduledPickupDate)} minDate={earliestAllowedDate} disabled={disableWorkflowButtons} onChange={(value) => handleScheduledPickupDateChange(value)} />
																		<Button variant='contained' color='secondary' disabled={disableWorkflowButtons} onClick={() => handleSchedulePickup("Redwood")}>Schedule Pickup</Button>
																	</p>
																</>
															}
															<p>&nbsp;</p>
															<p>Once you have scheduled a load for pickup you will no longer be able to break the seal.</p>
															<Spinner dependency={isLoadingCustomer || isLoadingTransporter || isLoadingVendor} text='Loading BOL...'>
																<BillOfLading load={load} transporter={transporter} vendor={vendor} customer={customer}></BillOfLading>
															</Spinner>
														</div>
													</div>
												}
												{stateManager.redwood.isAwaitingBol &&
													<div className="pending-vendor-bol">
														<div className="instructions">
															{errorOnSchedulePickup &&
																<Alert className="alert"
																	severity="error"
																	action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
																	{errorOnSchedulePickup}
																</Alert>
															}
															{isRedwoodShipmentCancelled ? (
																<>
																	<p className="title">
																		This load has been <strong>cancelled</strong> by Redwood.
																	</p>
																	<p className="cancelled-shipment-id">Please contact Redwood for more information. (Redwood Shipment ID: <strong>{load.vendorLoadId}</strong>)</p>
																	<p>From this step you can:</p>
																	<br></br>
																	<p>Return to <strong>Loading Status</strong> to fix any issues with the load.</p>
																	{isVerifyingReturnToLoading ? (
																		<p className="controls">
																			Are you sure you want to return to Loading status?
																			<Button variant='contained' color='primary' disabled={disableWorkflowButtons} onClick={() => { handleReturnToLoading() }}>Yes: Return to Loading Status</Button>
																			<Button variant='outlined' color='primary' disabled={disableWorkflowButtons} onClick={() => { setIsVerifyingReturnToLoading(false) }}>No: Cancel</Button>
																		</p>
																	) : (
																		<p className="controls">
																			<Button variant='contained' color='primary' disabled={disableWorkflowButtons} onClick={() => { setIsVerifyingReturnToLoading(true) }}>Return to Loading Status</Button>
																		</p>
																	)}
																	<div className="option-divider">
																		<hr className="line"></hr>
																		<p className="copy"><span>OR</span></p>
																	</div>
																	{isVerifyingDeleteLoad ? (
																		<p className="controls">
																			Are you sure you want to delete this load? <strong>Warning: This cannot be undone.</strong>
																			<Button variant='contained' color='error' disabled={disableWorkflowButtons} onClick={() => { handleDelete() }}>Yes: Delete this Load</Button>
																			<Button variant='outlined' color='error' disabled={disableWorkflowButtons} onClick={() => { setIsVerifyingDeleteLoad(false) }}>Cancel</Button>
																		</p>
																	) : (
																		<p className="controls">
																			<Button variant='contained' color='error' disabled={disableWorkflowButtons} onClick={() => { setIsVerifyingDeleteLoad(true) }}>Delete this Load</Button>
																		</p>
																	)}
																</>
															) : (
																<>
																	<p className="title">
																		This load is in <strong>Ready</strong> status.
																	</p>
																	<Alert className="alert-success"
																		severity="info">
																		This load is awaiting a Bill of Lading from Redwood. It will automatically transition to <strong>Scheduled</strong> status once received.
																	</Alert>
																	<Spinner dependency={isCheckingVendorBol} text='Checking status of Redwood shipment...'>
																		<p>The Redwood BOL is not yet available. Please check back later.</p>
																	</Spinner>
																</>
															)}
														</div>
													</div>
												}
											</>
										)}
										{(load.status?.toLowerCase() == "ready" && stateManager?.cirba) && (
											<>
												{stateManager.cirba.isReadyToSendWorkOrder &&
													<div className="ready">
														<div className="instructions">
															{errorOnSchedulePickup &&
																<Alert className="alert"
																	severity="error"
																	action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
																	{errorOnSchedulePickup}
																</Alert>
															}
															<p className="title">
																This load is in <strong>Ready</strong> status. From this step you can:
															</p>
															<br></br>
															<p>Break the seal. This will remove the seal number (if applicable) and return the load to "Loading" status where alterations can be made.</p>
															<p><Button variant='contained' color='error' disabled={disableWorkflowButtons} onClick={handleBreakSeal}>Break Seal</Button></p>
															<div className="option-divider">
																<hr className="line"></hr>
																<p className="copy"><span>OR</span></p>
															</div>
															<p>Send load information to Cirba. The system will generate a work order document and submit it to Cirba.</p>
															<p className="controls">
																<Button variant='contained' color='secondary' disabled={disableWorkflowButtons} onClick={handleSendWorkOrderToCirba}>Send Work Order to Cirba</Button>
															</p>
															<p>&nbsp;</p>
															<p>Once you have submitted the work order to Cirba you will no longer be able to break the seal.</p>
														</div>
													</div>
												}
												{stateManager.cirba.isWaitingToUploadBol &&
													<div className="pending-vendor-bol">
														<div className="instructions">
															{errorOnSchedulePickup &&
																<Alert className="alert"
																	severity="error"
																	action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
																	{errorOnSchedulePickup}
																</Alert>
															}
															<p className="title">
																This load is in <strong>Ready</strong> status.
															</p>
															<p>
																This load was sent to Cirba on <strong>{dayjs(load.loadDataSentToVendor).format('MMMM D, YYYY')}</strong> and is being processed.<br />
																<br />
																Once processing is complete, you will receive a Cirba BOL via email. Please use the form below to upload the Cirba BOL and continue processing this load through our normal workflow.<br />
																<br />
																The Cirba BOL will be emailed to the user who originally sent the load to Cirba for processing.
															</p>
															<Form onSubmit={handleUploadCirbaBol}>
																<p><TextField className='cirba-bol-number-field' placeholder='BOL Number' inputProps={{ maxLength: 25 }} disabled={disableWorkflowButtons} value={cirbaBolNumber} onChange={(e) => setCirbaBolNumber(e.target.value)}></TextField></p>
																<p className="upload-container">
																	<div className="custom-uploader">
																		<InputLabel htmlFor="cirbaBolUploader" className="uploader-label">Choose</InputLabel>
																		<input type="text" id="cirbaBolUploaderCaption" className={!cirbaBolFilename && "no-file-selected"} disabled={disableWorkflowButtons} readOnly value={cirbaBolFilename} placeholder='Select BOL Document'></input>
																	</div>
																	<input type="file" id="cirbaBolUploader" className="uploader" onChange={(event) => { handleCirbaBolFileChange(event) }} />
																</p>
																<p className="controls">
																	<Button type="submit" variant='contained' color='secondary' disabled={disableWorkflowButtons || (!cirbaBolNumber || !cirbaBolDocument)}>Upload BOL</Button>
																</p>
															</Form>
														</div>
													</div>
												}
												{stateManager.cirba.isWaitingToSchedulePickup &&
													<div className="pending-vendor-bol">
														<div className="instructions">
															{errorOnSchedulePickup &&
																<Alert className="alert"
																	severity="error"
																	action={<IconButton onClick={() => { setErrorOnSchedulePickup(undefined); }} size='small'>X</IconButton>}>
																	{errorOnSchedulePickup}
																</Alert>
															}
															<p className="title">
																This load is in <strong>Ready</strong> status. From this step you can:
															</p>
															<p>Contact the transporter to schedule pickup then enter the scheduled pickup date below.</p>
															<p className="controls">
																<DatePicker className="expiration-date-field" value={dayjs(scheduledPickupDate)} minDate={earliestAllowedDate} disabled={disableWorkflowButtons} onChange={(value) => handleScheduledPickupDateChange(value)} />
																<Button variant='contained' color='secondary' disabled={disableWorkflowButtons} onClick={() => handleSchedulePickup()}>Schedule Pickup</Button>
															</p>
															<Spinner dependency={isDownloadingVendorBol} text={`Loading ${stateManager.activeProfile} BOL...`}>
																{vendorBol ? (
																	<VendorDocumentViewer document={vendorBol} profileName={stateManager.activeProfile}></VendorDocumentViewer>
																) : (
																	<Alert className="alert"
																		severity="error">
																		Unable to retrieve the {stateManager.activeProfile} Bill of Lading #<strong>{load?.hwiNumber}</strong>. Please contact customer support for assistance.
																	</Alert>
																)}
															</Spinner>
														</div>
													</div>
												}
											</>
										)}
										{(load.status?.toLowerCase() == "scheduled") && (
											<div className="scheduled">
												<div className="instructions">
													{errorOnConfirmPickup &&
														<Alert className="alert"
															severity="error"
															action={<IconButton onClick={() => { setErrorOnConfirmPickup(undefined); }} size='small'>X</IconButton>}>
															{errorOnConfirmPickup}
														</Alert>
													}
													<p className="title">
														This load is in <strong>Scheduled</strong> status. From this step you can:
													</p>
													<br></br>
													{showLoadCommunicationActions &&
														<>
															<p>Communicate the status of this load.</p>
															<p className="communicate-controls">
																<StyledTooltip
																	title={<><strong>{vendor?.details.name || 'Vendor'}</strong> has no contact email address.</>}
																	placement='top'
																	disableHoverListener={vendorHasContactEmails()}
																	arrow>
																	<span> {/* Button needs to be wrapped in a span for the Tooltip to work correctly when the button is disabled */}
																		<Button variant='contained' color='primary' disabled={disableWorkflowButtons || !vendorHasContactEmails()} onClick={handleEmailLoadSheetToVendor}>Email Load Sheet to Vendor</Button>
																	</span>
																</StyledTooltip>
																<StyledTooltip
																	title={<><strong>{transporter?.details.name || 'Transporter'}</strong> has no contact email address.</>}
																	placement='top'
																	disableHoverListener={transporterHasContactEmails()}
																	arrow>
																	<span> {/* Button needs to be wrapped in a span for the Tooltip to work correctly when the button is disabled */}
																		<Button variant='contained' color='primary' disabled={disableWorkflowButtons || !transporterHasContactEmails()} onClick={handleNotifyTransporter}>Notify Transporter</Button>
																	</span>
																</StyledTooltip>
																<Button variant='contained' color='primary' disabled={disableWorkflowButtons} onClick={exportLoadSheet}>Download Load Sheet</Button>
															</p>
															<div className="option-divider">
																<hr className="line"></hr>
																<p className="copy"><span>OR</span></p>
															</div>
														</>
													}
													<Form onSubmit={handleConfirmPickup}>
														{requireSignedBol &&
															<>
																<p>Provide a signed BOL document by uploading a file or taking a picture with your device.</p>
																<div className="signed-bol-controls">
																	<p className="upload-container">
																		<div className="custom-uploader">
																			<InputLabel htmlFor="signedBolUploader" className="uploader-label">Choose</InputLabel>
																			<input
																				type="text"
																				id="signedBolUploaderCaption"
																				className={!signedBolDocFilename && "no-file-selected"}
																				disabled={disableWorkflowButtons}
																				readOnly
																				value={signedBolDocFilename}
																				placeholder='Select Signed BOL Document' />
																		</div>
																		<input
																			type="file"
																			id="signedBolUploader"
																			className="uploader"
																			accept={AppConfig.viewableDocumentExtensions.map(e => `.${e}`).join(", ")}
																			ref={signedBolDocRef}
																			onChange={(event) => { handleSignedBolDocChange(event) }} />
																		{" OR "}
																		<Button type="button" variant="contained" color="primary" onClick={() => { setShowSignedBolCameraDialog(true); }}>Take Picture</Button>
																	</p>
																</div>
																<br></br>
															</>
														}

														<p>Tell us what date the transporter picked up the load.</p>
														<p className="controls">
															<DatePicker className="expiration-date-field" value={dayjs(confirmedPickupDate)} minDate={earliestAllowedDate} disabled={disableWorkflowButtons} onChange={(value) => handleConfirmPickupDateChange(value)} />
															<Button type="submit" variant='contained' color='secondary' disabled={disableWorkflowButtons}>Confirm Pickup</Button>
														</p>
													</Form>
													{stateManager.isStandard || stateManager.didBypassVendorWorkflow ? (
														<Spinner dependency={isLoadingCustomer || isLoadingTransporter || isLoadingVendor} text='Loading BOL...'>
															<BillOfLading load={load} transporter={transporter} vendor={vendor} customer={customer}></BillOfLading>
														</Spinner>
													) : (
														<Spinner dependency={isDownloadingVendorBol} text={`Loading ${stateManager.activeProfile} BOL...`}>
															{vendorBol ? (
																<VendorDocumentViewer document={vendorBol} profileName={stateManager.activeProfile}></VendorDocumentViewer>
															) : (
																<Alert className="alert-success"
																	severity="error">
																	Unable to retrieve the {stateManager.activeProfile} Bill of Lading #<strong>{load?.hwiNumber}</strong>. Please contact customer support for assistance.
																</Alert>
															)}
														</Spinner>
													)}
												</div>
											</div>
										)}
										{(load.status?.toLowerCase() == "shipped" || load.status?.toLowerCase() == "legacy") && (
											<div className="shipped">
												<div className="instructions">
													<p className="title">
														This load has <strong>Shipped</strong>!
													</p>
													{load?.signedBolDocumentId ? (
														<DocumentViewer documentId={load.signedBolDocumentId} showBackButton={false}></DocumentViewer>
													) : (
														stateManager.isStandard || stateManager.didBypassVendorWorkflow ? (
															<Spinner dependency={isLoadingCustomer || isLoadingTransporter || isLoadingVendor} text='Loading BOL...'>
																<BillOfLading load={load} transporter={transporter} vendor={vendor} customer={customer}></BillOfLading>
															</Spinner>
														) : (
															<Spinner dependency={isDownloadingVendorBol} text={`Loading ${stateManager.activeProfile} BOL...`}>
																{vendorBol ? (
																	<VendorDocumentViewer document={vendorBol} profileName={stateManager.activeProfile}></VendorDocumentViewer>
																) : (
																	<Alert className="alert"
																		severity="error">
																		Unable to retrieve the {stateManager.activeProfile} Bill of Lading #<strong>{load?.hwiNumber}</strong>. Please contact customer support for assistance.
																	</Alert>
																)}
															</Spinner>
														)
													)}
												</div>
											</div>
										)}
									</div>
								}
							</Spinner>
						</LocalizationProvider>
					</Spinner>
				</PermissionGate>
			</Container>
			<WorkingModal params={workingModalParams?.params}></WorkingModal>
			{activeTab == "workflow" && load.status?.toLowerCase() == "scheduled" && requireSignedBol &&
				<CameraDialog
					showDialog={showSignedBolCameraDialog}
					onDialogCloseRequested={handeSignedBolCameraDialogCloseRequest}
					onSnapshotReady={handleSignedBolSnapshotReady}
				/>
			}
		</div >
	);
};

export default LoadEdit;